import {ChangeDetectionStrategy, Component, computed, ElementRef, Injectable, input, Signal, TemplateRef, viewChild} from "@angular/core";
import {GruppeId, MøteId, MøteModellEnkelMedProtokollinfo, MøteRessursClient, ProtokollDetaljerModell, TynnSakModell} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {Observable, switchMap, tap} from "rxjs";
import {takeUntilDestroyed, toObservable, toSignal} from "@angular/core/rxjs-interop";
import {MøteDatatjeneste} from "felles-ng2/tjenester/datalager/MøteDatatjeneste";
import {ProtokollDatatjeneste} from "felles-ng2/tjenester/datalager/ProtokollDatatjeneste";
import {MøteKortvisningMiniKomponent} from "../../../møte/komponenter/moete-kortvisning-mini/moete-kortvisning-mini";
import {TranslateModule} from "@ngx-translate/core";
import {ZonedDateTimeSomPlainDatePipe} from "felles-ng2/pipes/ZonedDateTimeSomPlainDatePipe";
import {FormaterPlainDatePipe} from "felles-ng2/pipes/FormaterPlainDatePipe";
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {MatDialog, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle} from "@angular/material/dialog";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatFormField, MatLabel} from "@angular/material/form-field";
import {MatOption} from "@angular/material/autocomplete";
import {MatSelect, MatSelectTrigger} from "@angular/material/select";
import {RessursAdresser} from "felles-ng2/tjenester/ressurser/RessursAdresser";
import {åpneINyttvindu} from "felles-ng2/støttefunksjoner/nettleser";
import {MatIcon} from "@angular/material/icon";
import {Navigasjon} from "web-ng2/tjenester/Navigasjon";
import {MøteprotokollStatusKomponent} from "../../../møteprotokoll/komponenter/møteprotokoll-status/møteprotokoll-status";
import {MatTooltip} from "@angular/material/tooltip";
import {TipsKomponent} from "web-ng2/delt/komponenter/tips/tips";
import {planperiodeSomTekst} from "felles-ng2/modell/planModellHjelpere";
import {GjeldendeTidssone} from "felles-ng2/tjenester/i18n/GjeldendeTidssone";
import {GjeldendeLokale} from "felles-ng2/tjenester/i18n/GjeldendeLokale";
import {ZonedDateTimePeriodeLukket} from "felles-ng2/typer/ZonedDateTimePeriode";
import {førsteVerdiLevertFraServer, mapTilData} from "felles-ng2/tjenester/datalager/DataOgMeta";
import {forTidligeProtokollbehandlinger, påfølgendeProtokollbehandlinger} from "felles-ng2/modell/møteModellHjelpere";
import {asynkArbeidPågårSignal} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {map} from "rxjs/operators";
import {InlineMeldingKomponent} from "web-ng2/delt/komponenter/inline-melding/inline-melding";
import {erIkkeTomListe} from "felles-ng2/støttefunksjoner/lister";
import {DokumentVisPdfDialogKomponent} from "web-ng2/moduler/dokument/modale/dokument-vis-pdf-dialog/dokument-vis-pdf-dialog";
import {EksterneLenker} from "felles-ng2/tjenester/EksterneLenker";

const debug = initDebugger('ProtokollerMedBehandlingerListingKomponent');


/**
 * Lister protokoller for møter i en plan samt informasjon om hvilke møter disse behandles i.
 */
@Component({
    selector: 'protokoller-med-behandlinger-listing',
    templateUrl: 'protokoller-med-behandlinger-listing.html',
    styleUrl: 'protokoller-med-behandlinger-listing.scss',
    imports: [
        MøteKortvisningMiniKomponent,
        TranslateModule,
        ZonedDateTimeSomPlainDatePipe,
        FormaterPlainDatePipe,
        ReactiveFormsModule,
        FormsModule,
        MatButton,
        MatDialogActions,
        MatDialogContent,
        MatDialogTitle,
        MatFormField,
        MatLabel,
        MatOption,
        MatSelect,
        MatSelectTrigger,
        MatIcon,
        MatIconButton,
        MøteprotokollStatusKomponent,
        MatTooltip,
        TipsKomponent,
        InlineMeldingKomponent
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProtokollerMedBehandlingerListingKomponent {
    static ajsSelector = 'protokollerMedBehandlingerListing';

    utvalg = input.required<GruppeOgPeriode>();

    laster = asynkArbeidPågårSignal();
    periodetittel = computed(() => planperiodeSomTekst(this.utvalg().periode, this.gjeldendeTidssone.brukersTidssone(), this.gjeldendeLokale.brukersLokale()));
    moeter: Signal<MøteOgProtokollinformasjon[]>;
    moeterMedforTidligBehandletProtokoll = computed(() => this.moeter().filter(møte => erIkkeTomListe(møte.forTidligeProtokollbehandlinger)));
    valgteMoeterKontroller = new FormControl<MøteModellEnkelMedProtokollinfo[]>([], {nonNullable: true, validators: Validators.required});

    private eksportDialogTemplate = viewChild.required<TemplateRef<ElementRef>>('eksportDialogTemplate');
    private eksportDialog: MatDialogRef<any> | null = null;

    constructor(private avhengigheter: ProtokollerMedBehandlingerListingKomponentAvhengigheter,
                private matDialog: MatDialog,
                private ressursAdresser: RessursAdresser,
                private navigasjon: Navigasjon,
                private gjeldendeTidssone: GjeldendeTidssone,
                private gjeldendeLokale: GjeldendeLokale,
                protected eksterneLenker: EksterneLenker) {
        this.moeter = toSignal(toObservable(this.utvalg)
          .pipe(
            tap(() => this.laster.set(true)),
            switchMap(utvalg => this.avhengigheter.møterOgProtokollbehandlinger(utvalg.gruppeId, utvalg.periode)),
            map(møter => møter.map(møte => this.beregnMøterOgProtokollinformasjon(møte))),
            tap(() => this.laster.set(false)),
            takeUntilDestroyed()
          ), {initialValue: []})
    }

    erValgt(a: MøteModellEnkelMedProtokollinfo, b: MøteModellEnkelMedProtokollinfo) {
        return a.moete.id === b.moete.id;
    }

    visEksporterDialog() {
        this.valgteMoeterKontroller.setValue(this.moeter());
        this.eksportDialog = this.matDialog.open(this.eksportDialogTemplate());
    }

    avbrytEksporter() {
        this.eksportDialog!.close();
    }

    eksporter() {
        this.eksportDialog!.close();
        const møteIder = this.valgteMoeterKontroller.value.map(innslag => innslag.moete.id)
        const url = this.ressursAdresser.protokollEksportZipUrl(møteIder);
        åpneINyttvindu(url);
    }

    async aapneForhaandsvisningProtokoll(innslag: MøteModellEnkelMedProtokollinfo) {
        const protokolldetaljer = await this.avhengigheter.protokolldetaljer(innslag.moete.id);
        DokumentVisPdfDialogKomponent.vis(this.matDialog, {dokumentversjon: protokolldetaljer.dokumentVersjon})
    }

    async lastNedProtokoll(innslag: MøteModellEnkelMedProtokollinfo) {
        const url = this.ressursAdresser.protokollPdfUrl(innslag.moete);
        åpneINyttvindu(url);
    }

    gaaTilBehandling(sak: TynnSakModell) {
        this.navigasjon.gåTilSak(sak.id);
    }

    gaaTilProtokoll(innslag: MøteModellEnkelMedProtokollinfo) {
        this.navigasjon.gåTilMøteProtokoll(innslag.moete.id);
    }

    //private hjelpemetoder

    private beregnMøterOgProtokollinformasjon(møter: MøteModellEnkelMedProtokollinfo): MøteOgProtokollinformasjon {
        return {
            ...møter,
            forTidligeProtokollbehandlinger: forTidligeProtokollbehandlinger(møter),
            paafoelgendeProtokollbehandlinger: påfølgendeProtokollbehandlinger(møter),
        };
    }
}

function opprettSkjemarad(møte: MøteModellEnkelMedProtokollinfo) {
    return new FormGroup({
        moeteinfo: new FormControl<MøteModellEnkelMedProtokollinfo>(møte),
        valgt: new FormControl<boolean>(false)
    });
}

interface MøteOgProtokollinformasjon extends MøteModellEnkelMedProtokollinfo {
    forTidligeProtokollbehandlinger: TynnSakModell[];
    paafoelgendeProtokollbehandlinger: TynnSakModell[];
}

@Injectable()
export class ProtokollerMedBehandlingerListingKomponentAvhengigheter {

    constructor(private møteDatatjeneste: MøteDatatjeneste, private møteRessurs: MøteRessursClient, private protokollDatatjeneste: ProtokollDatatjeneste, private a: RessursAdresser) {
    }

    møterOgProtokollbehandlinger(gruppeId: GruppeId, periode: ZonedDateTimePeriodeLukket): Observable<MøteModellEnkelMedProtokollinfo[]> {
        return this.møteDatatjeneste.finnMøterOgProtokollbehandlinger(gruppeId, periode)
          .pipe(mapTilData());
    }

    protokolldetaljer(møteId: MøteId): Promise<ProtokollDetaljerModell> {
        return førsteVerdiLevertFraServer(this.protokollDatatjeneste.hentMedMeta(møteId));
    }
}

export interface GruppeOgPeriode {
    gruppeId: GruppeId;
    periode: ZonedDateTimePeriodeLukket;
}
